import { ospThemes } from "@ospweb/sdk-ui";
import { useMemo, useRef } from "react";
import { cn } from "utils";
import bigDecimal from "js-big-decimal";
import { AssetTypeProps } from "./types";
import Translations from "@translate/components/Portfolio/AssetInsight/DistributionCard/AssetType";
import { I18nUtil } from "@utils/i18n";
import { PieChartDataItem } from "@/src/components/createChart";
import { usePieChart } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

const configColor = [
  {
    color: ospThemes.colors.content_brand_primary,
    hiddenColor: ospThemes.colors.content_brand_tertiary,
  },
  {
    color: ospThemes.colors.content_accent4_primary,
    hiddenColor: ospThemes.colors.content_accent4_tertiary,
  },
];

export const AssetType = ({ className, assetTypes }: AssetTypeProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const data: PieChartDataItem[] = useMemo(() => {
    if (!assetTypes) {
      return [];
    }
    let totalPercentage = "0";
    const processedData = assetTypes.map((item, index) => {
      const value = bigDecimal.round(
        item.value,
        2,
        bigDecimal.RoundingModes.DOWN,
      );
      totalPercentage = bigDecimal.add(totalPercentage, value);
      return {
        ...item,
        ...configColor[index % configColor.length],
        value: Number(value),
      };
    });
    // 如果总和不等于100，调整最后一个值
    if (processedData.length > 0 && Number(totalPercentage) !== 100) {
      const diff = bigDecimal.subtract(totalPercentage, "100");
      const latest = new bigDecimal(
        processedData[processedData.length - 1].value,
      ).getValue();
      processedData[processedData.length - 1].value = Number(
        bigDecimal.subtract(latest, diff),
      );
    }
    return processedData.filter((item) => item.value > 0);
  }, [assetTypes]);
  const { setHoverKey: setChartHoverKey } = usePieChart(chartRef, {
    data,
    centerContent: {
      value: data.length,
      label: I18n.t("Total"),
    },
    tooltipFormatter: (item) => `
      <div class="px-1.5 py-1 flex gap-0.5 bg-background_solid_quaternary rounded-1 items-center text-content_primary">
        <div class="body_s">${item.label}</div>
        <div class="body_medium_s">${item.value}%</div>
      </div>
    `,
  });
  return (
    <div className={cn("flex flex-col md:flex-row justify-between", className)}>
      <div className="w-80.25 h-52 md:h-46.5 md:w-61 flex items-center justify-center">
        <div className="w-48 h-48 md:w-39 md:h-39" ref={chartRef} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-x-[3px] auto-rows-max self-end items-end w-full md:w-40">
        {data.slice(0, 5).map((item) => (
          <div
            key={item.key}
            className="grid grid-cols-2 gap-x-3 py-1 px-3 max-h-7 md:hover:bg-background_white_overlay10 rounded-2 items-center"
            onMouseEnter={() => {
              // 鼠标进入图例时高亮对应扇区
              setChartHoverKey(item.key);
            }}
            onMouseLeave={() => {
              // 鼠标离开图例时恢复
              setChartHoverKey(null);
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full flex shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="body_m text-content_primary truncate">
                {item.label}
              </span>
            </div>
            <span className="body_medium_m text-content_primary">
              {item.value}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
